import { type BoardItemUpdate, type BoardItem, getBoardItemsByIds } from '@gorilla/common/src/lib/monday-api/api';
import { type MondayClient } from '@gorilla/common/src/lib/monday-api/common';

export type PortalCommentAttachment = {
  id: string;
  public_url: string;
  name: string;
  file_extension: string;
  file_size: number;
  token?: string;
  width?: number;
  height?: number;
};

export type PortalCommentReply = {
  id: string;
  text: string;
  creator_name: string;
  client_item_name?: string;
  created_at: string;
  metadata: CommentMetadata;
};

export type PortalComment = {
  id: string;
  text: string;
  created_at: string;
  creator_name: string;
  client_item_name?: string;
  attachments: PortalCommentAttachment[];
  replies: PortalCommentReply[];
  metadata: CommentMetadata;
};

export type ActivityLogEntry = {
  id: string;
  text: string;
  created_at: string;
  item_id?: number;
};

export type CommentMetadataFromClient = {
  type: 'from-client';
  clientItemId: number;
  name?: string;
};

export type CommentMetadataToClient = {
  type: 'to-client';
  clientItemId: number;
};

export type CommentMetadataClientActivity = {
  type: 'client-activity';
  clientItemId: number;
  name?: string;
};

export type CommentMetadataItemUpdate = {
  type: 'item-update';
};

export type CommentMetadata =
  CommentMetadataFromClient | CommentMetadataToClient | CommentMetadataClientActivity | CommentMetadataItemUpdate;

// regex that matches [from-client:<clientItemId>] or (<name>)[from-client:<clientItemId>]
const FROM_CLIENT_REGEX = /(?:\(([^)]+)\))?\[from-client:(\d+)\]/i;
const TO_CLIENT_REGEX = /\[to-client:(\d+)\]/i;
const CLIENT_ACTIVITY_REGEX = /(?:\(([^)]+)\))?\[client-activity:(\d+)\]/i;

export function extractCommentMetadata(text: string): CommentMetadata {
  const fromClientMatch = text.match(FROM_CLIENT_REGEX);
  if (fromClientMatch) {
    return {
      type: 'from-client',
      clientItemId: parseInt(fromClientMatch[2]!, 10),
      name: fromClientMatch[1] || undefined,
    };
  }

  const toClientMatch = text.match(TO_CLIENT_REGEX);
  if (toClientMatch) {
    return {
      type: 'to-client',
      clientItemId: parseInt(toClientMatch[1]!, 10),
    };
  }

  const clientActivityMatch = text.match(CLIENT_ACTIVITY_REGEX);
  if (clientActivityMatch) {
    return {
      type: 'client-activity',
      clientItemId: parseInt(clientActivityMatch[2]!, 10),
      name: clientActivityMatch[1] || undefined,
    };
  }

  return { type: 'item-update' };
}

export function getItemId(text: string) {
  // Check if the text contains [item:<clientItemId>]
  const regex = new RegExp(`\\[item:(\\d+)\\]`, 'i');
  const match = text.match(regex);
  if (match && match[1]) {
    // Check if the item ID is a number
    const itemIdNum = +match[1].trim();

    return itemIdNum;
  }

  return null;
}

export function removeClientTags(text: string) {
  // Remove (Name)[from-client:123] or [from-client:123] with trailing whitespace
  let result = text.replace(/(?:\([^)]+\))?\[from-client:\d+\]\s*/gi, '');
  // Remove [to-client:123] with trailing whitespace
  result = result.replace(/\[to-client:\d+\]\s*/gi, '');
  // Remove (Name)[client-activity:123] or [client-activity:123] with trailing whitespace
  result = result.replace(/(?:\([^)]+\))?\[client-activity:\d+\]\s*/gi, '');
  // Remove [item:123] with trailing whitespace
  return result.replace(/\[item:\d+\]\s*/gi, '');
}

function isClientComment(metadata: CommentMetadata): metadata is CommentMetadataFromClient | CommentMetadataToClient {
  return metadata.type === 'from-client' || metadata.type === 'to-client';
}

function parseGeometry(geometry?: string | null): { width?: number; height?: number } {
  if (!geometry) return {};
  const [w, h] = geometry.split('x').map(Number);
  if (!w || !h || isNaN(w) || isNaN(h)) return {};
  return { width: w, height: h };
}

export function boardItemUpdatesToPortalComments(
  updates: BoardItemUpdate[],
  clientItemId: number,
  filterMode: 'only-client' | 'all-clients' | 'all' = 'only-client',
): PortalComment[] {
  if ((filterMode !== 'only-client' && filterMode !== 'all-clients' && filterMode !== 'all') || !clientItemId) {
    console.warn('Invalid parameters provided to boardItemUpdatesToPortalComments');
    return [];
  }

  const comments = updates
    .filter((update) => {
      const metadata = extractCommentMetadata(update.text_body);
      if (filterMode === 'only-client') {
        return isClientComment(metadata) && metadata.clientItemId === clientItemId;
      } else if (filterMode === 'all-clients') {
        return isClientComment(metadata);
      } else if (filterMode === 'all') {
        return true;
      }

      return false;
    })
    .map((update) => {
      const metadata = extractCommentMetadata(update.text_body);

      return {
        id: update.id,
        text: removeClientTags(update.text_body),
        creator_name: update.creator?.name ?? 'Unknown',
        created_at: update.created_at,
        metadata,
        attachments: update.assets.map((asset) => ({
          id: asset.id,
          name: asset.name,
          public_url: asset.public_url,
          file_extension: asset.file_extension,
          file_size: asset.file_size,
          ...parseGeometry(asset.original_geometry),
        })),
        replies: update.replies
          .filter((reply) => {
            const replyMetadata = extractCommentMetadata(reply.text_body);
            if (filterMode === 'only-client') {
              return isClientComment(replyMetadata) && replyMetadata.clientItemId === clientItemId;
            } else if (filterMode === 'all-clients') {
              return isClientComment(replyMetadata);
            } else if (filterMode === 'all') {
              return true;
            }
            return false;
          })
          .map((reply) => {
            const replyMetadata = extractCommentMetadata(reply.text_body);
            return {
              id: reply.id,
              text: removeClientTags(reply.text_body),
              creator_name: reply.creator?.name ?? 'Unknown',
              created_at: reply.created_at,
              metadata: replyMetadata,
            };
          }),
      } as PortalComment;
    });

  return comments;
}

export function boardItemUpdatesToActivityLog(updates: BoardItemUpdate[], clientItemId: number): ActivityLogEntry[] {
  return updates
    .filter((update) => {
      const metadata = extractCommentMetadata(update.text_body);
      return metadata.type === 'client-activity' && metadata.clientItemId === clientItemId;
    })
    .map((update) => {
      const result: ActivityLogEntry = {
        id: update.id,
        text: removeClientTags(update.text_body),
        created_at: update.created_at,
      };

      const itemId = getItemId(update.text_body);

      if (itemId) {
        result.item_id = itemId;
      }

      return result;
    });
}

export async function addClientNameToComments(
  mondayClient: MondayClient,
  comments: PortalComment[],
  clientItem: BoardItem,
): Promise<PortalComment[]> {
  // Collect all unique clientItemIds from comments/replies with from-client metadata
  const unknownClientIds = new Set<number>();

  for (const comment of comments) {
    if (comment.metadata.type === 'from-client') {
      const clientId = comment.metadata.clientItemId;
      if (clientId !== clientItem.id) {
        unknownClientIds.add(clientId);
      }
    }
    for (const reply of comment.replies) {
      if (reply.metadata.type === 'from-client') {
        const clientId = reply.metadata.clientItemId;
        if (clientId !== clientItem.id) {
          unknownClientIds.add(clientId);
        }
      }
    }
  }

  // Build client name map starting with current clientItem
  const clientNameMap: Record<number, string> = {
    [clientItem.id]: clientItem.name,
  };

  // Fetch unknown client names if any
  if (unknownClientIds.size > 0) {
    const unknownItems = await getBoardItemsByIds(mondayClient, Array.from(unknownClientIds), '', {
      withoutBoardRelationValue: true,
      withoutMirrorValue: true,
    });
    for (const item of unknownItems) {
      clientNameMap[item.id] = item.name;
    }
  }

  // Add client_item_name to comments and replies with from-client metadata
  for (const comment of comments) {
    if (comment.metadata.type === 'from-client') {
      const meta = comment.metadata;
      // Use embedded name if available, otherwise look up in map
      comment.client_item_name = clientNameMap[meta.clientItemId];
    }
    for (const reply of comment.replies) {
      if (reply.metadata.type === 'from-client') {
        const meta = reply.metadata as CommentMetadataFromClient;
        reply.client_item_name = clientNameMap[meta.clientItemId];
      }
    }
  }

  return comments;
}
